import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./CloserPaywall.module.scss"

/**
 * CloserPaywall (`/closer`) — a self-contained NON-ONBOARDING upsell for Private
 * Photo Vault, staged as an optical FOCUS SCOPE you dive into. The whole screen is
 * a dark warm "viewing chamber" holding a single square viewport over one of your
 * own memories; the pitch is delivered by letting you fall *into* the photo and
 * discover how much of the moment is still in there — every drop, every face —
 * kept at full resolution, private, backed up, on every device.
 *
 * Divergence brief: claude-paywalls/_ledger.md · concept doc: claude-paywalls/closer.md
 *
 * Mechanic (new to the ledger): DIVE / spatial fly-in on the image itself. Pinch,
 * scroll, drag to pan, or press-and-hold the round DIVE control and the viewport
 * flies continuously *into* the photo — a live magnification readout climbs and
 * "found" tags bloom on details as you pass their depth ("that grin", "every
 * splash, frozen"). The scope teaches itself once on load with a slow cinematic
 * push-in, then hands the controls over. Purchase is never gated on the dive; the
 * dive only *shows* what Premium keeps whole.
 *
 * Value framing (honest, behavioural): REDISCOVERY + ENDOWMENT. Not "buy quality"
 * — "look how much of what you already have is still in here, and keep every one
 * of it." Register is intimate / marvelling ("the closer you look, the more is
 * there") — distinct from Aurora's cosmic awe and Abyss's calm-safety descent.
 *
 * Every ledger axis is chosen to differ:
 *   - Layout:      a single SQUARE OPTICAL VIEWPORT / focus scope in a dark
 *                  chamber, with a thin-line instrument HUD and a filmstrip of the
 *                  rest of the vault — not a hero card / split / receipt / calendar
 *                  / map / stage / panel.
 *   - Interaction: DIVE — direct spatial fly-in on the photo (pinch / scroll /
 *                  drag-pan / press-to-dive), distinct from every prior scrub /
 *                  hold-to-seal / flip / toss / dial / draw / haul / wind / lever.
 *   - Register:    intimate / marvelling — "there's a whole world in one photo."
 *   - Art:         near-black warm VIEWING CHAMBER so the photograph glows; brand
 *                  blue = the instrument HUD + featured yearly + CTA; warm amber =
 *                  the "found" detail tags. A single spotlit photo, photographic.
 *   - Value:       REDISCOVERY / your memories kept whole & explorable — full-res
 *                  originals, private & encrypted, auto-backup, every device.
 *
 * Honest & App Store compliant: the features are real Premium features (full-
 * resolution originals, private & encrypted, automatic backup, sync on every
 * device). Copy never implies photos are lost if the plan ends — it says the
 * opposite. Yearly is the featured, default plan and carries the most prominent
 * price; monthly is small and secondary. Save % is exact yearly-vs-12×-monthly
 * arithmetic; there is no countdown or fake scarcity. Photos are bundled demo
 * images already in the repo (no network egress).
 */

type PlanKey = "yearly" | "monthly"

interface Found {
  x: number // displayed-square fraction 0..1
  y: number
  t: string
  d: number // magnification at which it blooms
}
interface Shot {
  src: string
  label: string
  pos: string // object-position
  focus: { x: number; y: number; z: number } // auto-dive target
  finds: Found[]
}

// your vault — real bundled memories, highest-detail assets first so the dive
// stays crisp all the way in. Coords are in displayed (square-cropped) space.
const SHOTS: Shot[] = [
  {
    src: "/bound/beach.jpg",
    label: "At the shore",
    pos: "58% 50%",
    focus: { x: 0.58, y: 0.31, z: 2.6 },
    finds: [
      { x: 0.6, y: 0.67, t: "every splash, frozen", d: 1.5 },
      { x: 0.58, y: 0.31, t: "that grin", d: 2.1 },
      { x: 0.55, y: 0.6, t: "even the little duck", d: 2.8 },
    ],
  },
  {
    src: "/original/market.jpg",
    label: "The souk",
    pos: "50% 50%",
    focus: { x: 0.42, y: 0.6, z: 2.8 },
    finds: [
      { x: 0.42, y: 0.62, t: "every spice", d: 1.5 },
      { x: 0.33, y: 0.4, t: "labels on the jars", d: 2.3 },
      { x: 0.82, y: 0.86, t: "hand-woven, one by one", d: 3.1 },
    ],
  },
  {
    src: "/retouch/cake.jpg",
    label: "Ninth birthday",
    pos: "50% 42%",
    focus: { x: 0.6, y: 0.62, z: 2.7 },
    finds: [
      { x: 0.6, y: 0.64, t: "nine tiny flames", d: 1.5 },
      { x: 0.44, y: 0.32, t: "peeking through", d: 2.4 },
    ],
  },
  {
    src: "/reclaimed/beach.jpg",
    label: "Golden hour",
    pos: "55% 45%",
    focus: { x: 0.5, y: 0.42, z: 2.6 },
    finds: [
      { x: 0.5, y: 0.44, t: "every petal, printed", d: 1.6 },
      { x: 0.84, y: 0.5, t: "the flower she kept", d: 2.5 },
    ],
  },
]

const MIN_Z = 1
const MAX_Z = 3.4 // source assets ~1400px — cap so the dive stays crisp

const clamp = (v: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, v))

// keep the square image edges covering the viewport at any zoom
const clampFocus = (x: number, y: number, z: number) => {
  const m = 0.5 / z
  return { x: clamp(x, m, 1 - m), y: clamp(y, m, 1 - m) }
}

// real features that come with Premium — shown as compact HUD chips
const CHIPS = [
  { icon: "lock", label: "Private & encrypted" },
  { icon: "hd", label: "Full-quality originals" },
  { icon: "cloud", label: "Auto-backup" },
  { icon: "devices", label: "Every device" },
]

const ChipIcon = ({ name }: { name: string }) => {
  const c = {
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  }
  return (
    <svg viewBox="0 0 24 24" width={13} height={13} aria-hidden>
      {name === "lock" && (
        <>
          <rect x="5" y="10.5" width="14" height="9.5" rx="2.2" {...c} />
          <path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" {...c} />
        </>
      )}
      {name === "hd" && (
        <>
          <rect x="3.5" y="5" width="17" height="14" rx="2.4" {...c} />
          <path d="M7 9.5v5M7 12h2.4M9.4 9.5v5" {...c} />
          <path
            d="M12.8 9.5v5h1.8a2 2 0 0 0 2-2v-1a2 2 0 0 0-2-2h-1.8Z"
            {...c}
          />
        </>
      )}
      {name === "cloud" && (
        <path
          d="M7 18a4 4 0 0 1-.4-8 5.5 5.5 0 0 1 10.6 1.2A3.4 3.4 0 0 1 17 18H7Z"
          {...c}
        />
      )}
      {name === "devices" && (
        <>
          <rect x="3" y="6" width="12" height="9" rx="1.6" {...c} />
          <rect x="16" y="9" width="5" height="9" rx="1.4" {...c} />
          <path d="M6 18h6" {...c} />
        </>
      )}
    </svg>
  )
}

const CloserPaywall = () => {
  const { state, actions } = useAppState()
  const reduceMotion = useReducedMotion()

  // ── pricing (real debug data; yearly featured) ──────────────────────────────
  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"

  const yearlyTrial = yearlyProduct?.hasTrial
    ? (yearlyProduct?.introPricing?.period ?? 7)
    : 0
  const monthlyTrial = monthlyProduct?.hasTrial
    ? (monthlyProduct?.introPricing?.period ?? 3)
    : 0

  const savingsPct = useMemo(() => {
    const y = yearlyProduct?.price ?? 35.99
    const m = monthlyProduct?.price ?? 13.99
    if (!m) return 0
    return Math.max(0, Math.round((1 - y / (m * 12)) * 100))
  }, [yearlyProduct, monthlyProduct])

  // ── scope state ─────────────────────────────────────────────────────────────
  const [idx, setIdx] = useState(0)
  const shot = SHOTS[idx]

  const [zoom, setZoom] = useState(MIN_Z)
  const [focus, setFocus] = useState({ x: 0.5, y: 0.5 })
  const [plan, setPlan] = useState<PlanKey>("yearly")
  const [showClose, setShowClose] = useState(false)
  const [dived, setDived] = useState(false) // has the user driven the scope yet

  const frameRef = useRef<HTMLDivElement | null>(null)
  const raf = useRef<number | null>(null)
  const held = useRef(false)
  const drag = useRef<{ x: number; y: number } | null>(null)
  const pinch = useRef<{ dist: number; z: number } | null>(null)

  const stopRaf = () => {
    if (raf.current != null) cancelAnimationFrame(raf.current)
    raf.current = null
  }

  // ease (zoom, focus) toward a target — used by the teaching push-in
  const glideTo = useCallback(
    (tz: number, tx: number, ty: number, dur: number, onDone?: () => void) => {
      stopRaf()
      const t0 = performance.now()
      const z0 = zoomRef.current
      const f0 = { ...focusRef.current }
      const tf = clampFocus(tx, ty, tz)
      const tick = (now: number) => {
        const p = clamp((now - t0) / dur, 0, 1)
        const e = 1 - Math.pow(1 - p, 3) // easeOutCubic
        const z = z0 + (tz - z0) * e
        const nf = clampFocus(
          f0.x + (tf.x - f0.x) * e,
          f0.y + (tf.y - f0.y) * e,
          z,
        )
        zoomRef.current = z
        focusRef.current = nf
        setZoom(z)
        setFocus(nf)
        if (p < 1) raf.current = requestAnimationFrame(tick)
        else {
          raf.current = null
          onDone?.()
        }
      }
      raf.current = requestAnimationFrame(tick)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  // refs mirroring state so rAF/handlers read fresh values without re-binding
  const zoomRef = useRef(zoom)
  const focusRef = useRef(focus)
  zoomRef.current = zoom
  focusRef.current = focus

  const applyZoom = useCallback((z: number, fx?: number, fy?: number) => {
    const nz = clamp(z, MIN_Z, MAX_Z)
    const nf = clampFocus(
      fx ?? focusRef.current.x,
      fy ?? focusRef.current.y,
      nz,
    )
    zoomRef.current = nz
    focusRef.current = nf
    setZoom(nz)
    setFocus(nf)
  }, [])

  const markDived = () => {
    if (!held.current) stopRaf()
    setDived(true)
  }

  // press-and-hold DIVE — fly continuously inward, drifting toward the next
  // still-hidden detail so holding reveals the finds in order, then settling
  const diveLoop = useCallback(() => {
    if (!held.current) return
    const z = clamp(zoomRef.current * 1.02, MIN_Z, MAX_Z)
    const finds = SHOTS[idxRef.current].finds
    const target = finds.find((f) => f.d > zoomRef.current)
    let fx = focusRef.current.x
    let fy = focusRef.current.y
    if (target) {
      fx += (target.x - fx) * 0.05
      fy += (target.y - fy) * 0.05
    }
    applyZoom(z, fx, fy)
    raf.current = requestAnimationFrame(diveLoop)
  }, [applyZoom])

  const idxRef = useRef(idx)
  idxRef.current = idx

  const startDive = () => {
    native.logMixpanelEvent("html_closer_dive", { shot: shot.label })
    markDived()
    held.current = true
    stopRaf()
    raf.current = requestAnimationFrame(diveLoop)
  }
  const endDive = () => {
    held.current = false
    stopRaf()
  }

  const surface = () => {
    markDived()
    held.current = false
    glideTo(MIN_Z, 0.5, 0.5, 520)
  }

  // ── input: wheel / drag-pan / pinch on the viewport ─────────────────────────
  const onWheel = (e: React.WheelEvent) => {
    markDived()
    const factor = e.deltaY < 0 ? 1.12 : 1 / 1.12
    applyZoom(zoomRef.current * factor)
  }

  const onPointerDown = (e: React.PointerEvent) => {
    markDived()
    ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
    drag.current = { x: e.clientX, y: e.clientY }
  }
  const onPointerMove = (e: React.PointerEvent) => {
    if (!drag.current) return
    const f = frameRef.current
    if (!f) return
    const w = f.clientWidth || 1
    const dx = e.clientX - drag.current.x
    const dy = e.clientY - drag.current.y
    drag.current = { x: e.clientX, y: e.clientY }
    const z = zoomRef.current
    applyZoom(
      z,
      focusRef.current.x - dx / (w * z),
      focusRef.current.y - dy / (w * z),
    )
  }
  const onPointerUp = () => {
    drag.current = null
  }

  const onTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length !== 2) return
    markDived()
    const [a, b] = [e.touches[0], e.touches[1]]
    const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY)
    if (!pinch.current) {
      pinch.current = { dist, z: zoomRef.current }
      return
    }
    applyZoom((pinch.current.z * dist) / pinch.current.dist)
  }
  const onTouchEnd = (e: React.TouchEvent) => {
    if (e.touches.length < 2) pinch.current = null
  }

  // ── teaching push-in on load, then hand over ────────────────────────────────
  useEffect(() => {
    if (reduceMotion) {
      applyZoom(shot.focus.z, shot.focus.x, shot.focus.y)
      setShowClose(true)
      return
    }
    const kick = window.setTimeout(
      () => glideTo(shot.focus.z, shot.focus.x, shot.focus.y, 2200),
      520,
    )
    const back = window.setTimeout(
      () => glideTo(2.35, shot.focus.x, shot.focus.y, 900),
      2900,
    )
    const close = window.setTimeout(() => setShowClose(true), 1500)
    return () => {
      window.clearTimeout(kick)
      window.clearTimeout(back)
      window.clearTimeout(close)
      stopRaf()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduceMotion])

  // match the safe-area strips to the viewing chamber
  useEffect(() => {
    actions.setHeaderInsetColor("#0d0f14")
    actions.setFooterInsetColor("#0d0f14")
  }, [actions])

  // switching photos in the vault — reset the scope and re-teach a gentle dive
  const pickShot = (n: number) => {
    if (n === idx) return
    native.logMixpanelEvent("html_closer_pick", { shot: SHOTS[n].label })
    stopRaf()
    held.current = false
    setIdx(n)
    zoomRef.current = MIN_Z
    focusRef.current = { x: 0.5, y: 0.5 }
    setZoom(MIN_Z)
    setFocus({ x: 0.5, y: 0.5 })
    setDived(true)
    if (!reduceMotion) {
      const s = SHOTS[n]
      window.setTimeout(() => glideTo(2.1, s.focus.x, s.focus.y, 1200), 60)
    } else {
      applyZoom(SHOTS[n].focus.z, SHOTS[n].focus.x, SHOTS[n].focus.y)
    }
  }

  // keep the pinned focus point centred: origin at focus, then shift to centre —
  // see the header comment for the math.
  const stageStyle = useMemo(
    () => ({
      transform: `translate(${(0.5 - focus.x) * 100}%, ${
        (0.5 - focus.y) * 100
      }%) scale(${zoom})`,
      transformOrigin: `${focus.x * 100}% ${focus.y * 100}%`,
    }),
    [zoom, focus],
  )

  // ── plan + purchase wiring ──────────────────────────────────────────────────
  const choosePlan = (key: PlanKey) => {
    setPlan(key)
    native.logMixpanelEvent("html_pricing_toggle", { plan: key })
  }
  const handleClose = () => native.postNativeEvent("close")
  const purchase = () => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    native.logMixpanelEvent("html_closer_purchase", { plan, shot: shot.label })
    if (product) native.subscribe(product)
  }

  const trialDays = plan === "yearly" ? yearlyTrial : monthlyTrial
  const ctaSub =
    plan === "yearly"
      ? trialDays > 0
        ? `${trialDays} days free, then ${yearlyPrice}/yr · cancel anytime`
        : `${yearlyPrice}/yr · auto-renews · cancel anytime`
      : trialDays > 0
        ? `${trialDays} days free, then ${monthlyPrice}/mo · cancel anytime`
        : `${monthlyPrice}/mo · auto-renews · cancel anytime`

  const depthPct = ((zoom - MIN_Z) / (MAX_Z - MIN_Z)) * 100
  const hint = dived
    ? zoom > 1.35
      ? "Keep going — there's more in here"
      : "Pull in · drag to look around"
    : "Pinch, scroll, or hold ⌾ to dive in"

  return (
    <div className={styles.page}>
      <button
        className={`${styles.close} ${showClose ? styles.closeShown : ""}`}
        onClick={handleClose}
        aria-label="Close"
      >
        <svg viewBox="0 0 24 24" width={16} height={16} aria-hidden>
          <path
            d="M6 6l12 12M18 6L6 18"
            stroke="currentColor"
            strokeWidth={2.2}
            strokeLinecap="round"
          />
        </svg>
      </button>

      <header className={styles.head}>
        <span className={styles.brand}>
          <span className={styles.brandMark} aria-hidden>
            <svg viewBox="0 0 24 24" width={13} height={13}>
              <path
                d="M12 2.6l7 2.7v5.2c0 4.6-3 8.2-7 10.9-4-2.7-7-6.3-7-10.9V5.3l7-2.7Z"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinejoin="round"
              />
            </svg>
          </span>
          Private Photo Vault
        </span>
        <h1 className={styles.title}>
          There's a whole world inside one photo.
        </h1>
        <p className={styles.sub}>
          Keep every one at full resolution — private, automatically backed up,
          and on every device. Pull in and see how much is really still there.
        </p>
      </header>

      {/* ── the focus scope ────────────────────────────────────────────────────── */}
      <section className={styles.scopeWrap} aria-label="Photo focus scope">
        <div className={styles.magTag} aria-live="polite">
          ×{zoom.toFixed(1)}
          <span className={styles.magSub}>full resolution</span>
        </div>

        <div
          ref={frameRef}
          className={styles.frame}
          onWheel={onWheel}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <div className={styles.stage} style={stageStyle}>
            <img
              className={styles.shot}
              src={shot.src}
              alt={shot.label}
              style={{ objectPosition: shot.pos }}
              draggable={false}
            />
            {shot.finds.map((f, i) => {
              // bloom as you reach a detail, fade once you've dived well past it
              const on = zoom >= f.d && zoom <= f.d + 1.3
              return (
                <span
                  key={`${idx}-${i}`}
                  className={`${styles.find} ${on ? styles.findOn : ""}`}
                  style={{ left: `${f.x * 100}%`, top: `${f.y * 100}%` }}
                >
                  <span
                    className={styles.findInner}
                    style={{
                      transform: `translate(-50%,-50%) scale(${1 / zoom})`,
                    }}
                  >
                    <span className={styles.findDot} />
                    <span className={styles.findText}>{f.t}</span>
                  </span>
                </span>
              )
            })}
          </div>

          {/* fixed instrument overlay */}
          <span className={styles.vignette} aria-hidden />
          <span className={styles.reticle} aria-hidden>
            <i /> <i /> <i /> <i />
          </span>
          <span className={styles.grain} aria-hidden />

          <span className={styles.depth} aria-hidden>
            <span
              className={styles.depthFill}
              style={{ height: `${depthPct}%` }}
            />
          </span>
          <span className={styles.hint}>{hint}</span>
        </div>

        {/* dive control + vault filmstrip */}
        <div className={styles.controls}>
          <button
            className={`${styles.dive} ${zoom > 1.35 ? styles.diveHot : ""}`}
            onPointerDown={(e) => {
              e.preventDefault()
              startDive()
            }}
            onPointerUp={endDive}
            onPointerLeave={endDive}
            onPointerCancel={endDive}
            aria-label="Hold to dive in"
          >
            <span className={styles.diveRing} aria-hidden />
            <svg viewBox="0 0 24 24" width={20} height={20} aria-hidden>
              <circle
                cx="11"
                cy="11"
                r="6.4"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              />
              <path
                d="M11 8.4v5.2M8.4 11h5.2M15.8 15.8l3.4 3.4"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
              />
            </svg>
          </button>

          <div className={styles.strip} role="tablist" aria-label="Your vault">
            {SHOTS.map((s, i) => (
              <button
                key={s.src}
                className={`${styles.thumb} ${i === idx ? styles.thumbOn : ""}`}
                onClick={() => pickShot(i)}
                aria-label={s.label}
                aria-selected={i === idx}
                role="tab"
              >
                <img src={s.src} alt="" style={{ objectPosition: s.pos }} />
              </button>
            ))}
          </div>

          <button
            className={styles.surface}
            onClick={surface}
            aria-label="Pull back out"
          >
            <svg viewBox="0 0 24 24" width={17} height={17} aria-hidden>
              <path
                d="M6 15l6-6 6 6"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <ul className={styles.chips}>
          {CHIPS.map((c) => (
            <li key={c.label} className={styles.chip}>
              <ChipIcon name={c.icon} />
              {c.label}
            </li>
          ))}
        </ul>
      </section>

      {/* ── plans — yearly featured, monthly small & secondary ─────────────────── */}
      <div className={styles.plans}>
        <button
          className={`${styles.planYear} ${plan === "yearly" ? styles.sel : ""}`}
          onClick={() => choosePlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          {savingsPct > 0 && (
            <span className={styles.saveBadge}>SAVE {savingsPct}%</span>
          )}
          <span className={styles.planMain}>
            <span className={styles.radio} aria-hidden />
            <span className={styles.planNameWrap}>
              <span className={styles.planName}>Yearly</span>
              <span className={styles.planNote}>
                {yearlyTrial > 0
                  ? `${yearlyTrial}-day free trial included`
                  : "Your whole vault, all year"}
              </span>
            </span>
          </span>
          <span className={styles.planPrice}>
            <span className={styles.planBig}>{yearlyPrice}</span>
            <span className={styles.planPer}>/yr</span>
            <span className={styles.planSmall}>{yearlyPerMonth}/mo</span>
          </span>
        </button>

        <button
          className={`${styles.planMonth} ${
            plan === "monthly" ? styles.selMonth : ""
          }`}
          onClick={() => choosePlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.planMonthName}>Monthly</span>
          <span className={styles.planMonthPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <button className={styles.cta} onClick={purchase}>
        <span className={styles.ctaLabel}>
          {plan === "yearly" ? "Keep every photo" : "Start monthly"}
        </span>
        <span className={styles.ctaSub}>{ctaSub}</span>
      </button>

      <p className={styles.reassure}>
        Your photos are always yours. Nothing is ever deleted — keep every one
        at full quality, private and backed up.
      </p>

      <div className={styles.legal}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span aria-hidden>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span aria-hidden>·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </div>
    </div>
  )
}

export default CloserPaywall
